import React, {useRef, useState} from 'react'
import {
  Button,
  Grid,
  Paper,
  Box,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Checkbox,
} from '@material-ui/core'
import {ToggleButtonGroup, ToggleButton} from '@material-ui/lab'
import RefreshIcon from '@material-ui/icons/Refresh'
import SearchIcon from '@material-ui/icons/Search'
import {Query} from 'react-apollo'
import LoadingScreen from '../BarcodeLoadingScreen'
import {distinctKeyObjects} from '../../graphql/barcode/queries/distinctKeyObjectsQuery'
import '../../styles/BarcodeFieldFilter.css'
import {useTranslation} from 'react-i18next'
import useStyles from './BarcodeFieldColumnFilter.styles'

export default function BarcodeFieldColumnFilter(props) {
  const classes = useStyles()
  const {t} = useTranslation()
  const [sorting, setSorting] = useState('asc')
  const [formValues, setFormValues] = useState({
    search: '',
  })
  const [selected, setSelected] = useState([])
  const [page, setPage] = useState(0)
  const [rowsPerPage] = useState(50)
  const [loadingObject, setLoadingObject] = useState(true)
  const userId = parseInt(window.localStorage.getItem('user_id'))
  const startIndex = page * 50
  const endIndex = startIndex + 50
  const hasFetched = useRef(false)

  function handleSelectAllClick(event) {
    if (event && event.target.checked) {
      const newSelecteds = props.fieldObjects.slice(startIndex, endIndex)
      setSelected(newSelecteds)
      return
    }
    setSelected([])
  }

  function handleClick(event, name) {
    const selectedIndex = selected.indexOf(name)
    let newSelected = []

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1))
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      )
    }
    setSelected(newSelected)
  }

  function handleChangePage(event, newPage) {
    setPage(newPage)
  }

  function resetButtonClick(event) {
    setFormValues({
      ...formValues,
      search: '',
    })
    setSelected([])
    setSorting('asc')
    props.setPageOffset(0)
    props.setLoadedObjects([])
    props.setSelected([])
    props.setObjectSort('')
    props.setFilterValues({})
  }

  function applyBtnClick(event) {
    props.setFieldMatch('full')

    const filterKey = props.columnFilterSearch.key
    props.setObjectSort(sorting === 'asc' ? `+${filterKey}` : `-${filterKey}`)

    let searchFilters = {}

    const selectedValues = selected
      .map(item => {
        const value = item?.[filterKey]
        if (value && typeof value === 'object' && value.display_name) {
          return value.display_name
        } else if (typeof value === 'string' || typeof value === 'number') {
          return value
        }
        return null
      })
      .filter(Boolean)

    if (selectedValues.length > 0) {
      searchFilters[filterKey] = selectedValues.join('|') + '|'
    }

    if (formValues.search !== '') {
      if (!searchFilters[filterKey]) {
        searchFilters[filterKey] = `${formValues.search}|`
      } else {
        searchFilters[filterKey] += `${formValues.search}|`
      }
    }

    props.setPageOffset(0)
    props.setLoadedObjects([])
    props.setSelected([])
    props.setFilterValues(searchFilters)
  }

  const handleChange = textField => event => {
    setFormValues({
      ...formValues,
      [textField]: event.target.value,
    })
  }

  const handleSorting = (event, newSorting) => {
    if (newSorting) {
      setSorting(newSorting)
    }
  }

  const isSelected = name => selected.findIndex(sel => sel.id === name.id) !== -1

  if (props.columnFilterSearch) {
    return (
      <Query
        query={distinctKeyObjects}
        variables={{
          entityId: parseInt(props.entityId),
          token: props.token,
          fieldId: parseInt(props.columnFilterSearch.fieldId),
          userId: userId,
        }}
      >
        {({loading, error, data}) => {
          if (loading) {
            setLoadingObject(true)
            return (
              <div style={{marginTop: 35}}>
                <LoadingScreen size={50} />
              </div>
            )
          }
          if (error) {
            return <div>{t('barcode.wizard.column-filter.error-loading')}</div>
          }

          if (data && data['distinctKeyObjects'] && !hasFetched.current) {
            props.setFieldObjects(data['distinctKeyObjects']['objects'])
            hasFetched.current = true
            setLoadingObject(false)
          }

          setLoadingObject(false)

          return (
            <div>
              <Paper className={classes.paper}>
                <Grid container justify="flex-end">
                  <Button size="small" style={{textTransform: 'none'}} onClick={resetButtonClick}>
                    <Box color="#9e9e9e">{t('barcode.wizard.column-filter.reset')}</Box>
                    <RefreshIcon style={{width: 16, height: 16, color: '#9e9e9e'}} />
                  </Button>
                </Grid>

                <Box marginBottom={2} />

                <Grid container>
                  <TextField
                    id="outlined-name"
                    label="Search"
                    value={formValues.search}
                    fullWidth
                    variant="outlined"
                    onChange={handleChange('search')}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment>
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      style: {height: 40},
                    }}
                    InputLabelProps={{
                      classes: {
                        root: classes.labelTransform,
                      },
                    }}
                  />
                </Grid>

                <Box marginBottom={2} />

                <Grid container>
                  <Box fontSize={14} fontWeight={300}>
                    {t('barcode.wizard.column-filter.sort-alphanumerically')}
                  </Box>
                </Grid>
                <Grid container justify="center">
                  <ToggleButtonGroup
                    value={sorting}
                    exclusive
                    onChange={handleSorting}
                    className={classes.toggleButtonMargin}
                  >
                    <ToggleButton
                      value="asc"
                      className={classes.toggleBtnStyle}
                      style={{
                        backgroundColor: sorting === 'asc' ? '#2f65ab' : '',
                        color: sorting === 'asc' ? 'white' : 'black',
                      }}
                    >
                      {t('barcode.wizard.column-filter.ascending')}
                    </ToggleButton>

                    <ToggleButton
                      value="desc"
                      className={classes.toggleBtnStyle}
                      style={{
                        backgroundColor: sorting === 'desc' ? '#2f65ab' : '',
                        color: sorting === 'desc' ? 'white' : 'black',
                      }}
                    >
                      {t('barcode.wizard.column-filter.descending')}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>

                <Box marginBottom={2} />

                <Grid container hidden={props?.fieldObjects?.length === 0}>
                  <Box fontSize={14} fontWeight={300}>
                    {t('barcode.wizard.column-filter.show')}
                  </Box>
                  <Paper className={classes.paperTable}>
                    <Table className={classes.table}>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              indeterminate={
                                selected.length > 0 && selected.length < props?.fieldObjects?.length
                              }
                              checked={
                                selected.length === props?.fieldObjects?.length &&
                                props?.fieldObjects?.length !== 0
                              }
                              onChange={handleSelectAllClick}
                              inputProps={{'aria-label': 'select all'}}
                            />
                            {t('barcode.wizard.column-filter.select-all')}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                    </Table>

                    <div
                      style={{
                        height: 150,
                        overflow: 'auto',
                        overflowY: 'auto',
                      }}
                    >
                      <Table className={classes.table} aria-labelledby="tableTitle" size="small">
                        <TableBody>
                          {props?.fieldObjects
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((row, index) => {
                              const isItemSelected = isSelected(row)
                              const labelId = `enhanced-table-checkbox-${index}`

                              // Get key to show (from columnFilterSearch or another source)
                              const key = props.columnFilterSearch?.key
                              const value = row[key]

                              // Get display value based on type
                              const displayValue =
                                typeof value === 'object' && value !== null
                                  ? value.display_name || JSON.stringify(value)
                                  : value

                              return (
                                <TableRow
                                  hover
                                  onClick={event => handleClick(event, row)}
                                  role="checkbox"
                                  aria-checked={isItemSelected}
                                  tabIndex={-1}
                                  key={`enhanced-table-row-${index}`}
                                  selected={isItemSelected}
                                >
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      checked={isItemSelected}
                                      inputProps={{
                                        'aria-labelledby': labelId,
                                      }}
                                      style={{marginRight: 10}}
                                    />
                                    {displayValue || '-'}
                                  </TableCell>
                                </TableRow>
                              )
                            })}
                        </TableBody>
                      </Table>
                    </div>

                    <TablePagination
                      hidden={loadingObject}
                      rowsPerPageOptions={[]}
                      count={props?.fieldObjects?.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      backIconButtonProps={{
                        'aria-label': 'previous page',
                      }}
                      nextIconButtonProps={{
                        'aria-label': 'next page',
                      }}
                      onChangePage={handleChangePage}
                      className={classes.MuiTablePaginationActions}
                    />
                  </Paper>
                </Grid>

                <Box marginBottom={2} />
              </Paper>
              <Grid container>
                <Button
                  value="apply"
                  variant="contained"
                  className={classes.buttonLink}
                  color="primary"
                  fullWidth
                  onClick={applyBtnClick}
                >
                  {t('barcode.wizard.column-filter.apply')}
                </Button>
              </Grid>
            </div>
          )
        }}
      </Query>
    )
  } else {
    return <div />
  }
}
